import React, { useState, useEffect } from "react";
import Input from "../../../components/ui/input";
import Button from "../../../components/ui/button";
import Table from "../../../components/ui/table";
import styles from "./notes.module.css";
import { MyIcons } from "../../../components/icons/my-icons";
import Dropdown from "../../../components/ui/dropdown";
import Modal from "../../../components/ui/modal";
import FolderSelectorModal from "../../../components/modals/FolderSelectorModal";
import SambaFilePickerModal from "../../../components/modals/SambaFilePickerModal";
import { useOutletContext, useParams } from "react-router-dom";
import { getAnnotationsSummary, replyEmail, getGmailStatus, createNote, downloadEmailAttachments, getPredefinedEmailText } from "../../../services/apiService";
import { API_BASE_URL } from "../../../config";
import { showSuccessToast, showErrorToast } from "../../../utils/notifications";
import { formatDateToSpanish } from "../../../utils/dateUtils";
import usePageTitle from "../../../hooks/usePageTitle";
import UnsavedChangesModal from "../../../components/modals/unsaved-changes-modal";
import useUnsavedChangesWarning from "../../../hooks/useUnsavedChangesWarning";

const Notes = () => {
  const { id: expedienteId } = useParams();
  usePageTitle(`Expediente ${expedienteId}`, 'Notas');
  const { recordData, isLockedByOther } = useOutletContext();
  const [filterOption, setFilterOption] = useState(null);
  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);
  const [isNoteModalOpen, setIsNoteModalOpen] = useState(false);
  const [isViewNoteModalOpen, setIsViewNoteModalOpen] = useState(false);
  const [isFolderModalOpen, setIsFolderModalOpen] = useState(false);
  const [notesTableData, setNotesTableData] = useState([]);
  const [selectedNote, setSelectedNote] = useState(null);
  const [pendingDownloadNote, setPendingDownloadNote] = useState(null);
  const [attachments, setAttachments] = useState([]);
  const [showSambaFilePicker, setShowSambaFilePicker] = useState(false);
  const [emailFormData, setEmailFormData] = useState({
    recipient: "",
    subject: "",
    description: "",
    content: "",
    gmailMessageId: "",
    to: "",
    cc: "",
    bcc: ""
  });
  const [noteFormData, setNoteFormData] = useState({
    subject: "",
    content: "",
  });
  const [selectedLanguage, setSelectedLanguage] = useState({ value: "ESP", label: "Castellano" });
  const [selectedNoteLang, setSelectedNoteLang] = useState({ value: "ESP", label: "Castellano" });

  // Estados para detectar cambios sin guardar
  const [initialEmailFormData, setInitialEmailFormData] = useState(null);
  const [initialNoteFormData, setInitialNoteFormData] = useState(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Hook para advertir cambios sin guardar
  const { showModal, handleCancel, handleConfirm, message } = useUnsavedChangesWarning(
    hasUnsavedChanges,
    "Tienes cambios sin guardar en el formulario."
  );

  const handleFilterChange = (option) => {
    setFilterOption(option);
  };

  // useEffect para detectar cambios sin guardar en formulario de email
  useEffect(() => {
    if (isEmailModalOpen && initialEmailFormData) {
      const emailFormChanged = JSON.stringify(emailFormData) !== JSON.stringify(initialEmailFormData);
      const attachmentsChanged = JSON.stringify(attachments) !== JSON.stringify([]);
      setHasUnsavedChanges(emailFormChanged || attachmentsChanged);
    } else if (isNoteModalOpen && initialNoteFormData) {
      const noteFormChanged = JSON.stringify(noteFormData) !== JSON.stringify(initialNoteFormData);
      setHasUnsavedChanges(noteFormChanged);
    } else {
      setHasUnsavedChanges(false);
    }
  }, [emailFormData, attachments, isEmailModalOpen, initialEmailFormData, noteFormData, isNoteModalOpen, initialNoteFormData]);

  // Filtrar datos de la tabla según la opción seleccionada
  const filteredNotesData = React.useMemo(() => {
    if (!filterOption || filterOption.value === "all") {
      return notesTableData;
    }
    
    return notesTableData.filter(note => {
      const noteTipo = note.tipo.toLowerCase();
      if (filterOption.value === "email") {
        return noteTipo === "email";
      } else if (filterOption.value === "note") {
        return noteTipo === "anotación" || noteTipo === "anotacion";
      }
      return true;
    });
  }, [notesTableData, filterOption]);

  const handleNoteClick = (note) => {
    console.log("Note clicked:", note);

    // Extraer texto limpio para asunto y contenido (evitar usar los nodos truncados usados en la tabla)
    const extractText = (value) => {
      if (value == null) return "";
      if (typeof value === "string") return value;
      // Si es un elemento React (p.ej. <div className={styles.truncatedCell}>text</div>)
      if (typeof value === "object") {
        return (value.props && value.props.children) ? value.props.children : "";
      }
      return String(value);
    };

    const asunto = note.asuntoOriginal ?? extractText(note.asunto);
    const contenido = note.contenidoOriginal ?? extractText(note.contenido);

    // Siempre abrir el modal de visualización con textos completos
    setSelectedNote({ ...note, asunto, contenido });
    setIsViewNoteModalOpen(true);
  };

  const handleReplyEmail = async () => {
    // Configurar datos para responder email
    const asuntoOriginal = selectedNote.asuntoOriginal || selectedNote.asunto;
    const asuntoStr = typeof asuntoOriginal === 'string' ? asuntoOriginal : '';
    
    const initialEmailData = {
      recipient: selectedNote.usuario,
      subject: asuntoStr.startsWith("Re: ") ? asuntoStr : `Re: ${asuntoStr}`,
      description: "",
      content: "",
      gmailMessageId: selectedNote.gmailMessageId,
      to: selectedNote.toAddresses || "",
      cc: selectedNote.ccAddresses || "",
      bcc: selectedNote.bccAddresses || ""
    };
    
    setEmailFormData(initialEmailData);
    setInitialEmailFormData(initialEmailData);
    
    // Cerrar modal de visualización y abrir modal de respuesta
    setIsViewNoteModalOpen(false);
    setIsEmailModalOpen(true);

    // Cargar texto predefinido según idioma seleccionado
    try {
      const predefinedTexts = await getPredefinedEmailText();
      if (predefinedTexts && predefinedTexts.length > 0) {
        // Buscar la plantilla del idioma seleccionado
        const predefinedText = predefinedTexts.find(t => t.id === selectedLanguage.value) || predefinedTexts[0];
        setEmailFormData(prev => ({
          ...prev,
          content: predefinedText.body || ""
        }));
        setInitialEmailFormData(prev => ({
          ...prev,
          content: predefinedText.body || ""
        }));
      }
    } catch (error) {
      console.error('Error loading predefined email text:', error);
      // No mostrar error ya que es opcional
    }
  };

  const handleCloseViewNoteModal = () => {
    setIsViewNoteModalOpen(false);
    setSelectedNote(null);
  };

  const handleDownload = (note) => {
    console.log("Iniciando descarga de adjuntos del email:", note);
    
    // Solo procesar si tiene adjuntos y es un email con gmailMessageId
    if (note.hasAdjuntos === "No" || !note.gmailMessageId) {
      showErrorToast("Esta anotación no tiene archivos adjuntos");
      return;
    }

    // Guardar la nota y abrir el modal para seleccionar carpeta
    setPendingDownloadNote(note);
    setIsFolderModalOpen(true);
  };

  const handleFolderSelected = async (nombreCarpeta) => {
    if (!pendingDownloadNote) return;

    try {
      console.log("Guardando adjuntos en SAMBA para gmailMessageId:", pendingDownloadNote.gmailMessageId);
      console.log("Carpeta seleccionada:", nombreCarpeta);
      
      showSuccessToast("Guardando adjuntos en el servidor...");
      
      // Llamar a la API para guardar los adjuntos en SAMBA
      const response = await downloadEmailAttachments(
        pendingDownloadNote.gmailMessageId, 
        parseInt(expedienteId.toString().replace(/\s+/g, '')), 
        nombreCarpeta
      );
      
      console.log("Respuesta de la API:", response);
      
      if (!response.savedFiles || response.savedFiles.length === 0) {
        showErrorToast("No se encontraron archivos adjuntos para este email");
        setPendingDownloadNote(null);
        return;
      }
      
      const totalFiles = response.savedFiles.length;
      
      // Mostrar información de los archivos guardados
      console.log(`✓ ${totalFiles} archivo(s) guardado(s) en SAMBA:`, response.savedFiles);
      
      // Crear mensaje con los nombres de los archivos guardados
      const fileNames = response.savedFiles.map(f => f.fileName).join(', ');
      const message = totalFiles === 1 
        ? `Archivo guardado en SAMBA: ${fileNames}`
        : `${totalFiles} archivos guardados en SAMBA: ${fileNames}`;
      
      showSuccessToast(message);
      setPendingDownloadNote(null);
      
    } catch (error) {
      console.error("Error guardando adjuntos:", error);
      const errorMessage = error?.response?.data?.message || error?.message || "Error al guardar los adjuntos en el servidor";
      showErrorToast(errorMessage);
      setPendingDownloadNote(null);
    }
  };

  const handleEmailFormChange = (e) => {
    const { name, value } = e.target;
    setEmailFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleLanguageChange = async (language) => {
    setSelectedLanguage(language);
    
    // Recargar plantilla con el nuevo idioma
    try {
      const predefinedTexts = await getPredefinedEmailText();
      if (predefinedTexts && predefinedTexts.length > 0) {
        const predefinedText = predefinedTexts.find(t => t.id === language.value) || predefinedTexts[0];
        setEmailFormData(prev => ({
          ...prev,
          content: predefinedText.body || ""
        }));
      }
    } catch (error) {
      console.error('Error loading predefined email text:', error);
    }
  };

  const handleSambaFilesSelect = (fileObjects) => {
    // fileObjects es un array de objetos con {path, name, size}
    // Extraer solo los paths como strings
    const newPaths = fileObjects.map(file => {
      if (typeof file === 'string') return file;
      if (file && typeof file === 'object' && file.path) return file.path;
      return null;
    }).filter(path => path); // Remover nulls
    
    // Añadir solo los que no existen ya (evitar duplicados)
    setAttachments(prevFiles => {
      const existingPaths = prevFiles.filter(f => typeof f === 'string');
      const filesToAdd = newPaths.filter(path => !existingPaths.includes(path));
      return [...prevFiles, ...filesToAdd];
    });
  };

  // Función para formatear tamaños de archivo
  const formatFileSize = (bytes) => {
    if (!bytes || bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  const removeAttachment = (index) => {
    setAttachments(prevFiles => prevFiles.filter((_, i) => i !== index));
  };

  const resetEmailForm = () => {
    setEmailFormData({
      recipient: "",
      subject: "",
      description: "",
      content: "",
      gmailMessageId: "",
      to: "",
      cc: "",
      bcc: ""
    });
    setAttachments([]);
    setSelectedNote(null);
  };

  const handleCloseModal = () => {
    setIsEmailModalOpen(false);
    setInitialEmailFormData(null);
    setHasUnsavedChanges(false);
    resetEmailForm();
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    console.log("Email datos:", emailFormData);
    console.log("Attachments:", attachments);
    
    try {
      // Obtener el ID del usuario actual del localStorage
      const usuarioId = localStorage.getItem("usuarioId");
      
      if (!usuarioId) {
        showErrorToast("Error: No se pudo obtener el ID del usuario");
        return;
      }

      // Validar tamaño de archivos adjuntos (límite de Gmail: 25MB)
      // Calcular el tamaño total de los archivos
      if (attachments.length > 0) {
        const token = localStorage.getItem('authToken');
        let totalSize = 0;
        
        // Sumar el tamaño de todos los archivos
        for (const file of attachments) {
          // Si el archivo tiene el tamaño directo, usarlo
          if (file.size) {
            totalSize += file.size;
          } else {
            // Si no tiene tamaño, intentar obtenerlo desde el servidor
            try {
              const path = typeof file === 'string' ? file : file.path;
              const response = await fetch(`${API_BASE_URL}/api/files/size?path=${encodeURIComponent(path)}`, {
                headers: token ? { 'Authorization': `Bearer ${token}` } : {}
              });
              
              if (response.ok) {
                const data = await response.json();
                totalSize += data.size || 0;
              }
            } catch (error) {
              console.error('Error obteniendo tamaño de archivo:', file, error);
            }
          }
        }
        
        const maxSizeBytes = 25 * 1024 * 1024; // 25 MB en bytes
        
        if (totalSize > maxSizeBytes) {
          const totalSizeMB = (totalSize / (1024 * 1024)).toFixed(2);
          showErrorToast(`El tamaño total de los archivos adjuntos (${totalSizeMB} MB) excede el límite de Gmail de 25 MB. Por favor, reduce el número de archivos o su tamaño.`);
          return;
        }
        
        console.log(`Tamaño total de archivos adjuntos: ${(totalSize / (1024 * 1024)).toFixed(2)} MB`);
      }

      const emailData = {
        gmailMessageId: emailFormData.gmailMessageId,
        descripcion: emailFormData.description,
        mensaje: emailFormData.content,
        usuarioId: parseInt(usuarioId),
        files: attachments.map(file => {
          // Asegurar que siempre enviamos strings
          if (typeof file === 'string') return file;
          if (file && typeof file === 'object' && file.path) return file.path;
          return null;
        }).filter(path => path), // Remover nulls
        to: emailFormData.to || '',
        cc: emailFormData.cc || '',
        bcc: emailFormData.bcc || ''
      };

      console.log("Sending email data:", JSON.stringify(emailData, null, 2));
      console.log("gmailMessageId:", emailFormData.gmailMessageId);
      console.log("description:", emailFormData.description);
      console.log("content:", emailFormData.content);
      console.log("files:", attachments);

      await replyEmail(emailData);
      showSuccessToast("Email enviado correctamente");
      
      // Recargar anotaciones
      const annotations = await getAnnotationsSummary(parseInt(expedienteId.toString().replace(/\s+/g, '')));
      const formattedAnnotations = annotations.map(note => ({
        ...note,
        fecha: formatDateToSpanish(note.fecha),
        tipo: note.tipo.charAt(0).toUpperCase() + note.tipo.slice(1),
        hasAdjuntos: note.hasAdjuntos ? "Sí" : "No",
        asuntoOriginal: note.asunto,
        contenidoOriginal: note.contenido,
        asunto: <div className={styles.truncatedCell}>{note.asunto}</div>,
        contenido: <div className={styles.truncatedCell}>{note.contenido}</div>,
      }));
      setNotesTableData(formattedAnnotations);
      
      // Cerrar modal y limpiar formulario
      setInitialEmailFormData(null);
      setHasUnsavedChanges(false);
      handleCloseModal();
    } catch (error) {
      console.error("Error sending email:", error);
      console.error("Error details:", {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        message: error.message
      });
      
      if (error.response?.data?.message === "GMAIL_NOT_AUTHORIZED") {
        showErrorToast("Error: Gmail no autorizado. Por favor, reautoriza Gmail desde Herramientas.");
      } else if (error.response?.status === 400) {
        const errorMsg = error.response?.data?.message || "Datos inválidos. Verifica que todos los campos estén completos.";
        showErrorToast(`Error 400: ${errorMsg}`);
      } else {
        showErrorToast("Error al enviar el email");
      }
    }
  };

  const handleNoteFormChange = (e) => {
    const { name, value } = e.target;
    setNoteFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const resetNoteForm = () => {
    setNoteFormData({
      subject: "",
      content: "",
    });
  };

  const handleCloseNoteModal = () => {
    setIsNoteModalOpen(false);
    setInitialNoteFormData(null);
    setHasUnsavedChanges(false);
    resetNoteForm();
  };

  const handleCreateNote = () => {
    const initialNote = {
      subject: "",
      content: "",
    };
    setNoteFormData(initialNote);
    setInitialNoteFormData(initialNote);
    setIsNoteModalOpen(true);
  };

  const handleNoteSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const usuarioId = localStorage.getItem("usuarioId");
      
      if (!usuarioId) {
        showErrorToast("Error: No se pudo obtener el ID del usuario");
        return;
      }

      const noteData = {
        expedienteId: parseInt(expedienteId.toString().replace(/\s+/g, '')),
        asunto: noteFormData.subject,
        contenido: noteFormData.content,
        usuarioId: parseInt(usuarioId),
      };

      await createNote(noteData);
      showSuccessToast("Nota creada correctamente");
      
      // Recargar anotaciones
      const annotations = await getAnnotationsSummary(parseInt(expedienteId.toString().replace(/\s+/g, '')));
      const formattedAnnotations = annotations.map(note => ({
        ...note,
        fecha: formatDateToSpanish(note.fecha),
        tipo: note.tipo.charAt(0).toUpperCase() + note.tipo.slice(1),
        hasAdjuntos: note.hasAdjuntos ? "Sí" : "No",
        asuntoOriginal: note.asunto,
        contenidoOriginal: note.contenido,
        asunto: <div className={styles.truncatedCell}>{note.asunto}</div>,
        contenido: <div className={styles.truncatedCell}>{note.contenido}</div>,
      }));
      setNotesTableData(formattedAnnotations);
      
      // Cerrar modal y limpiar formulario
      setInitialNoteFormData(null);
      setHasUnsavedChanges(false);
      handleCloseNoteModal();
    } catch (error) {
      console.error("Error creating note:", error);
      showErrorToast("Error al crear la nota");
    }
  };

  const filterOptions = [
    { value: "all", label: "Todos" },
    { value: "email", label: "Email" },
    { value: "note", label: "Anotación" },
  ];

  const languageOptions = [
    { value: "ESP", label: "Castellano" },
    { value: "CAT", label: "Catalán" },
  ];

  const notesTableHeaders = [
    { label: "Fecha", key: "fecha" },
    { label: "Asunto", key: "asunto" },
    { label: "Tipo", key: "tipo" },
    { label: "Usuario", key: "usuario" },
    { label: "Contenido", key: "contenido" },
    { label: "Adjuntos", key: "hasAdjuntos" },
  ];

  // Cargar anotaciones desde la API
  useEffect(() => {
    const fetchAnnotations = async () => {
      if (expedienteId) {
        try {
          const annotations = await getAnnotationsSummary(parseInt(expedienteId.toString().replace(/\s+/g, '')));
          console.log("Annotations loaded:", annotations);
          
          // Formatear datos para la tabla
          const formattedAnnotations = annotations.map(note => ({
            ...note,
            fecha: formatDateToSpanish(note.fecha),
            tipo: note.tipo.charAt(0).toUpperCase() + note.tipo.slice(1), // Capitalizar tipo
            hasAdjuntos: note.hasAdjuntos ? "Sí" : "No",
            asuntoOriginal: note.asunto,
            contenidoOriginal: note.contenido,
            asunto: <div className={styles.truncatedCell}>{note.asunto}</div>,
            contenido: <div className={styles.truncatedCell}>{note.contenido}</div>,
          }));
          
          setNotesTableData(formattedAnnotations);
        } catch (error) {
          console.error("Error loading annotations:", error);
          if (error.response?.data?.message === "GMAIL_NOT_AUTHORIZED") {
            showErrorToast("Error: Gmail no autorizado. Por favor, reautoriza Gmail desde Herramientas.");
          } else {
            showErrorToast("Error al cargar las anotaciones");
          }
        }
      }
    };

    fetchAnnotations();
  }, [expedienteId]);

  // Verificar estado de Gmail cada hora
  useEffect(() => {
    const checkGmailStatus = async () => {
      try {
        const status = await getGmailStatus();
        if (!status.authorized) {
          showErrorToast("Gmail no está autorizado. Por favor, reautoriza desde Herramientas > Gmail > Reautorizar.");
        }
      } catch (error) {
        console.error("Error checking Gmail status:", error);
      }
    };

    // Verificar inmediatamente
    checkGmailStatus();

    // Verificar cada hora (3600000 ms)
    const interval = setInterval(checkGmailStatus, 3600000);

    return () => clearInterval(interval);
  }, []);

  return (
    <div style={{ paddingTop: "24px" }}>
      <div className={styles.searchContainer}>
        <Dropdown
          options={filterOptions}
          onSelect={handleFilterChange}
          placeholder="Filtrar por tipo"
          selected={filterOption}
          style={{ height: "25px" }}
        />
      </div>

      <div className={styles.tableContainer}>
        <Table
          headers={notesTableHeaders}
          data={filteredNotesData}
          onPress={isLockedByOther ? null : handleNoteClick}
          renderActions={(row) => (
            <div className={styles.actionButtons}>
              {row.hasAdjuntos === "Sí" && row.gmailMessageId && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDownload(row);
                  }}
                  className={styles.actionButton}
                  title="Guardar adjuntos en expediente"
                >
                  <MyIcons
                    name="downloadPdf"
                    width={20}
                    height={20}
                    color={"var(--black)"}
                  />
                </button>
              )}
            </div>
          )}
        />
      </div>
      <div className={styles.buttonsContainer}>
        <Button variant="secondary" disabled={isLockedByOther} onClick={handleCreateNote}>CREAR ANOTACION</Button>
      </div>

      <Modal
        isOpen={isNoteModalOpen}
        onClose={handleCloseNoteModal}
        title="Crear Nota"
      >
        <form onSubmit={handleNoteSubmit} className={styles.emailForm}>
          <Input
            id="subject"
            name="subject"
            value={noteFormData.subject}
            onChange={handleNoteFormChange}
            placeholder="Asunto"
            required
            style={{width: "600px"}}
          />

          <div style={{marginBottom: "16px"}}>
            <label style={{display: "block", marginBottom: "8px", fontWeight: "500"}}>Corrector ortográfico</label>
            <Dropdown
              options={languageOptions}
              selected={selectedNoteLang}
              onSelect={setSelectedNoteLang}
              placeholder="Idioma del corrector"
              style={{width: "200px"}}
            />
          </div>

          <textarea
            id="content"
            name="content"
            value={noteFormData.content}
            onChange={handleNoteFormChange}
            placeholder="Escribe el contenido aquí..."
            required
            className={styles.emailContent}
            rows={8}
            spellCheck={selectedNoteLang.value !== "CAT"}
            lang={selectedNoteLang.value === "CAT" ? "ca" : "es"}
          />

          <Button type="submit" style={{width: "fit-content", alignSelf: "center"}}>Crear Nota</Button>
        </form>
      </Modal>

      <Modal
        isOpen={isEmailModalOpen}
        onClose={handleCloseModal}
        title="Responder Email"
        disableClickOutside={showSambaFilePicker}
      >
        <form onSubmit={handleEmailSubmit} className={styles.emailForm}>
          <Input
            id="recipient"
            name="recipient"
            value={emailFormData.recipient}
            onChange={handleEmailFormChange}
            placeholder="Correo electrónico"
            style={{width: "600px"}}
            readOnly
          />

          <Input
            id="subject"
            name="subject"
            value={emailFormData.subject}
            onChange={handleEmailFormChange}
            placeholder="Asunto"
            style={{width: "600px"}}
            readOnly
          />

          <Input
            id="description"
            name="description"
            value={emailFormData.description}
            onChange={handleEmailFormChange}
            placeholder="Descripción"
            required
            style={{width: "600px"}}
          />

          <Input
            id="to"
            name="to"
            value={emailFormData.to}
            onChange={handleEmailFormChange}
            placeholder="Para (To)"
            style={{width: "600px"}}
          />

          <Input
            id="cc"
            name="cc"
            value={emailFormData.cc}
            onChange={handleEmailFormChange}
            placeholder="CC (correos separados por coma)"
            style={{width: "600px"}}
          />

          <Input
            id="bcc"
            name="bcc"
            value={emailFormData.bcc}
            onChange={handleEmailFormChange}
            placeholder="CCO/BCC (correos separados por coma)"
            style={{width: "600px"}}
          />

          <div style={{marginBottom: "16px"}}>
            <label style={{display: "block", marginBottom: "8px", fontWeight: "500"}}>Idioma de la plantilla</label>
            <Dropdown
              options={languageOptions}
              selected={selectedLanguage}
              onSelect={handleLanguageChange}
              placeholder="Selecciona idioma"
              style={{width: "200px"}}
            />
          </div>

          <textarea
            id="content"
            name="content"
            value={emailFormData.content}
            onChange={handleEmailFormChange}
            placeholder="Escribe el contenido aquí..."
            required
            className={styles.emailContent}
            rows={8}
            spellCheck={selectedLanguage.value !== "CAT"}
            lang={selectedLanguage.value === "CAT" ? "ca" : "es"}
          />

          <div className={styles.attachmentContainer}>
            <label className={styles.attachmentLabel}>Archivos adjuntos</label>
            <Button
              onClick={() => setShowSambaFilePicker(true)}
              variant="secondary"
              type="button"
              style={{width: "fit-content"}}
            >
              Seleccionar archivos
            </Button>
            
            {attachments.length > 0 && (
              <div className={styles.attachmentList}>
                {attachments.map((file, index) => {
                  const fileName = typeof file === 'string' ? file.split('/').pop() : file.name;
                  const fileSize = typeof file === 'object' && file.size ? formatFileSize(file.size) : null;
                  return (
                    <div key={index} className={styles.attachmentItem}>
                      <div className={styles.fileInfo}>
                        <span className={styles.fileName}>{fileName}</span>
                        {fileSize && <span className={styles.fileSize}>({fileSize})</span>}
                      </div>
                      <button
                        type="button"
                        onClick={() => removeAttachment(index)}
                        className={styles.removeButton}
                      >
                        ✕
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <Button type="submit" style={{width: "fit-content", alignSelf: "center"}}>Enviar</Button>
        </form>
      </Modal>

      <Modal
        isOpen={isViewNoteModalOpen}
        onClose={handleCloseViewNoteModal}
        title={selectedNote?.tipo === "Email" ? "Detalles del email" : "Detalle de Anotación"}
      >
        {selectedNote && (
          <div className={styles.noteDetailContainer}>
            <div className={styles.noteDetailRow}>
              <strong>Fecha:</strong>
              <span>{formatDateToSpanish(selectedNote.fecha)}</span>
            </div>
            <div className={styles.noteDetailRow}>
              <strong>Asunto:</strong>
              <span>{selectedNote.asunto}</span>
            </div>
            <div className={styles.noteDetailRow}>
              <strong>Usuario:</strong>
              <span>{selectedNote.usuario}</span>
            </div>
            <div className={styles.noteDetailRow}>
              <strong>Adjuntos:</strong>
              <span>{selectedNote.hasAdjuntos}</span>
              {selectedNote.hasAdjuntos === "Sí" && selectedNote.gmailMessageId && (
                <Button 
                  onClick={() => handleDownload(selectedNote)} 
                  variant="secondary"
                  style={{marginLeft: "10px", padding: "4px 12px", fontSize: "12px"}}
                >
                  Guardar en Expediente
                </Button>
              )}
            </div>
            <div className={styles.noteDetailContent}>
              <strong>Contenido:</strong>
              <p>{selectedNote.contenido}</p>
            </div>
            {selectedNote.tipo === "Email" && (
              <div className={styles.replyButtonContainer}>
                <Button onClick={handleReplyEmail} style={{width: "fit-content", alignSelf: "center"}}>
                  Responder
                </Button>
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* Modal para seleccionar carpeta para adjuntos */}
      <FolderSelectorModal
        isOpen={isFolderModalOpen}
        onClose={() => {
          setIsFolderModalOpen(false);
          setPendingDownloadNote(null);
        }}
        onFolderSelect={handleFolderSelected}
        expId={parseInt(expedienteId.toString().replace(/\s+/g, ''))}
      />

      {/* Modal de selección de archivos SAMBA */}
      <SambaFilePickerModal
        isOpen={showSambaFilePicker}
        onClose={() => setShowSambaFilePicker(false)}
        onFilesSelect={handleSambaFilesSelect}
        expId={parseInt(expedienteId.toString().replace(/\s+/g, ''))}
        expedienteYear={expedienteId ? Math.floor(parseInt(expedienteId.toString().replace(/\s+/g, '')) / 10000) + 2000 : new Date().getFullYear()}
        multiple={true}
      />

      {/* Modal de advertencia de cambios sin guardar */}
      <UnsavedChangesModal
        isOpen={showModal}
        onCancel={handleCancel}
        onConfirm={handleConfirm}
        message={message}
      />
    </div>
  );
};

export default Notes;
